import React, { useState, useEffect } from "react"
import { Button, Checkbox, Flex, FormControl, Text } from "@chakra-ui/react"
import Turnstile from "react-turnstile"
import { useForm } from "@app/hooks/useForm"
import { FormInput } from "@app/components/Form/FormInput"
import { FormTextarea } from "@app/components/Form/FormTextarea"
import { ReviewAddStarRatingInput } from "@app/components/Product/Review/Add/ReviewAddStarRatingInput"
import { useFunctions } from "@app/hooks/useFunctions"
import { Loader } from "@app/components/Loader/Loader"
import ReviewAddFormComplete from "@app/components/Product/Review/Add/ReviewAddFormComplete"
import { graphql, useStaticQuery } from "gatsby"
import type { ProductCombinedProps } from "@root/types/global"

import { useConfigContext } from "@app/providers/config"

type ReviewData = {
  sku: string
  product_title: string
  product_description: string
  display_name: string
  email: string
  review_content: string
  review_title: string
  review_score: string
}

type Props = {
  product: ProductCombinedProps
  onClose: () => void
}

export const ReviewAddForm: React.FC<Props> = ({ product, onClose }) => {
  const {
    services: { cloudflare },
  } = useConfigContext()

  const { locales } = useStaticQuery<GatsbyTypes.ReviewSectionFormAddQuery>(graphql`
    query ReviewSectionFormAddQuery {
      locales: sanityTemplateProduct {
        additionReviewDrawFormTermsAndConditions
        additionReviewDrawFormTermsAndConditionsAdditionalInfo
        reviewDrawFormRating
        reviewDrawFormPlaceholderEmail
        additionReviewDrawFormPlaceholderTitle
        reviewDrawFormPlaceholderComment
        additionReviewFormPlaceholderAuthor
        additionReviewDrawFormSubmit
      }
    }
  `)
  const [formComplete, setFormComplete] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const INITIAL_STATE = {
    title: "",
    author: "",
    comment: "",
    email: "",
    rating: "0",
    termsAndConditions: "false",
  }
  const { data, handleChange } = useForm(INITIAL_STATE)
  const { callFunction } = useFunctions()

  // Turnstile States
  const [disabled, setDisabled] = useState(true)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const [widgetId, setWidgetId] = useState<string | null>(null)

  useEffect(() => {
    if (isSubmitting && error) {
      setDisabled(true)
      if (widgetId) window?.turnstile?.reset(widgetId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, error])

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setIsSubmitting(true)
    data.rating = data.rating.toString()

    if (data.rating == "0") {
      setIsSubmitting(false)
      setError("Please select a rating")
      return
    }
    const reviewData: ReviewData = {
      sku: product?.legacyId || "",
      product_title: product?.title,
      product_description: product?.description,
      display_name: data.author,
      email: data.email,
      review_content: data.comment,
      review_title: data.title,
      review_score: data.rating,
    }
    const { status, error } = await callFunction("create-reviews", { productReview: reviewData, ["cf-turnstile-token"]: turnstileToken })

    if (status === "error") {
      setIsSubmitting(false)
      setError(error)
      return
    } else if (status === "success") {
      setIsSubmitting(false)
      setFormComplete(true)
    }
  }
  const submitForm = async (event: React.BaseSyntheticEvent) => {
    await handleSubmit(event)
  }

  return !isSubmitting && !formComplete ? (
    <Flex justifyContent="center" as="form" onSubmit={submitForm} px={{ base: 6, md: 14 }} py={8}>
      <Flex width={124} flexDirection={"column"}>
        <Flex flexDirection="column" alignItems={"center"}>
          <Text fontSize="2xl" color="grey.text">
            {locales?.reviewDrawFormRating}
          </Text>
          <FormControl mb={4} mt={4} as={Flex} justifyContent={"center"}>
            <ReviewAddStarRatingInput value={data.rating} onChange={handleChange} />
          </FormControl>
        </Flex>
        <FormControl isRequired mb={4}>
          <FormInput
            background="grey.cloud"
            color="grey.text"
            borderRadius="8px"
            onChange={handleChange}
            name={"title"}
            id="title"
            placeholder={locales?.additionReviewDrawFormPlaceholderTitle}
            value={data?.title}
          />
        </FormControl>

        <FormControl isRequired mb={4}>
          <FormTextarea
            background={"grey.cloud"}
            color="grey.text"
            borderRadius={"8px"}
            onChange={handleChange}
            name={"comment"}
            id="comment"
            placeholder={locales?.reviewDrawFormPlaceholderComment}
            value={data?.comment}
          />
        </FormControl>

        <FormControl mb={4}>
          <FormInput
            background={"grey.cloud"}
            color="grey.text"
            value={data.author}
            onChange={handleChange}
            name={"author"}
            id="author"
            placeholder={locales?.additionReviewFormPlaceholderAuthor}
            borderRadius={"8px"}
          />
        </FormControl>

        <FormControl mb={4}>
          <FormInput
            value={data.email}
            borderRadius={"8px"}
            onChange={handleChange}
            name={"email"}
            color="grey.text"
            id="email"
            type="email"
            placeholder={locales?.reviewDrawFormPlaceholderEmail}
            background={"grey.cloud"}
          />
        </FormControl>

        <FormControl mb={4}>
          <Checkbox
            onChange={handleChange}
            name={"termsAndConditions"}
            id={"termsAndConditions"}
            sx={{
              ".chakra-checkbox__control": {
                background: "grey.white",
              },
            }}
          >
            {locales?.additionReviewDrawFormTermsAndConditions}
          </Checkbox>
        </FormControl>
        <Text fontSize={"xs"} color="grey.text">
          {locales?.additionReviewDrawFormTermsAndConditionsAdditionalInfo}
        </Text>
        {(error || !!turnstileError) && (
          <Text color="brand.error" fontSize="sm" mt={4}>
            {turnstileError || error}
          </Text>
        )}
        <Button
          mt={4}
          pt={4}
          pb={3}
          px={3}
          colorScheme="teal"
          type="submit"
          variant={"pdpAddToCart"}
          isDisabled={data.termsAndConditions == "false" || disabled}
        >
          {locales?.additionReviewDrawFormSubmit}
        </Button>

        {cloudflare?.turnstile_key ? (
          <Turnstile
            sitekey={cloudflare.turnstile_key}
            onVerify={token => {
              setTurnstileToken(token)
              setDisabled(false)
              setTurnstileError(null)
            }}
            onLoad={widgetId => {
              setWidgetId(widgetId)
              setDisabled(true)
            }}
            onExpire={() => {
              setDisabled(true)
              if (widgetId) window?.turnstile?.reset(widgetId)
            }}
            onError={error => {
              setDisabled(true)
              setTurnstileError(`Verification failed (${error}). Please refresh the page and try again.`)
            }}
          />
        ) : null}
      </Flex>
    </Flex>
  ) : isSubmitting && !formComplete ? (
    <Loader> </Loader>
  ) : (
    <ReviewAddFormComplete onClose={onClose} />
  )
}
